import { Configuration, ErrorCode } from '@linktivity/triplabo-taxi';
import { auth } from '@triplabo/web/stores';
import { showToast } from '@triplabo/web/components';
import { handleLogin } from '@triplabo/web/utils';
import i18n from '@triplabo/web/i18n';

const BASE_PATH = import.meta.env.VITE_APP_WEB_API;
const AUTHORIZATION_HEADER = 'grpc-metadata-auth-token';
const SIGNATURE_HEADER = 'grpc-metadata-signature';
const MAX_RETRY_ATTEMPTS = 3;

const getRefreshedToken = async () => {
  const token = await auth.getRefreshedToken();
  return token;
};

const handleUnauthorizedError = async (
  init: RequestInit,
  url: string,
  fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>
): Promise<Response | void> => {
  try {
    const token = await getRefreshedToken();
    if (token) {
      const requestHeaders = new Headers(init.headers);
      requestHeaders.set(AUTHORIZATION_HEADER, token);
      return fetch(url, { ...init, headers: requestHeaders });
    } else {
      handleLogin();
    }
  } catch {
    handleLogin();
  }
};

export const retryRequest = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = MAX_RETRY_ATTEMPTS
): Promise<T> => {
  let retryCount = 0;
  let retryPaymentCount = 0;
  const retryPaymentDelay = 1000;

  const tryFetch = async (): Promise<T> => {
    try {
      return await fn();
    } catch (error: unknown) {
      console.log(`Api error:`, error);
      // 判断错误类型，检查是否是超时错误
      const isDeadlineExceeded =
        (error as { id?: ErrorCode })?.id === ErrorCode.DeadlineExceeded;

      // 判断错误类型，支付失败错误
      const isPaymentFailed =
        (error as { id?: ErrorCode })?.id === ErrorCode.PaymentFailed;

      if (isDeadlineExceeded) {
        retryCount++;

        if (retryCount >= maxRetries) {
          showToast({
            message: i18n.t('errorCode.DeadlineExceeded')
          });
          throw error;
        }

        return tryFetch();
      } else if (isPaymentFailed) {
        // 支付失败重试（1秒后重试，3次不成功直接抛出错误）
        retryPaymentCount++;

        if (retryPaymentCount >= maxRetries) {
          showToast({
            message: i18n.t('errorCode.PaymentFailed')
          });
          throw error;
        }

        await new Promise(resolve => setTimeout(resolve, retryPaymentDelay));
        return tryFetch();
      }

      // 其他错误直接抛出
      throw error;
    }
  };

  return tryFetch();
};

const config = new Configuration({
  basePath: BASE_PATH,
  middleware: [
    {
      pre: async context => {
        const requestHeaders: HeadersInit = new Headers({
          'Content-Type': 'application/json'
        });
        // 如果有signature就只取signature；否则就取accessToken；两块都没有就重新登录
        if (auth.signature) {
          requestHeaders.append(SIGNATURE_HEADER, auth.signature);
        } else if (auth.accessToken) {
          requestHeaders.append(AUTHORIZATION_HEADER, auth.accessToken);
        }
        context.init.headers = requestHeaders;
      },
      post: async context => {
        const { response, url, init, fetch } = context;
        if (!response.ok) {
          const data = await response.json();
          if (data?.id === ErrorCode.AccessTokenExpired) {
            return await handleUnauthorizedError(init, url, fetch);
          } else if (data?.id === ErrorCode.Unauthorized) {
            handleLogin();
          } else if (data?.id === ErrorCode.DeadlineExceeded) {
            throw {
              id: data.id,
              errorData: data
            };
          } else {
            const errorMessage = i18n.t(`errorCode.${data?.id}`);
            if (errorMessage) {
              showToast({ message: errorMessage });
            } else {
              if (data?.common?.error_description || data?.common?.error_code) {
                showToast({
                  message:
                    data?.common?.error_description || data?.common?.error_code
                });
              }
            }
            throw new Error('API error');
          }
        }
      }
    }
  ]
});

export default config;
